import { CircleAlert, CircleCheck } from 'lucide-react'
import type { TmsInvoiceRow } from '@numeo/types-main-web-app'
import { DocumentChips } from './DocumentChips'
import { formatUsd } from './format'
import { LoadFixActions } from './LoadFixActions'
import { findingsOf, REASON_LABEL, rowActions } from './rowActions'
import { StatusBadge } from './StatusBadge'
import { useInvoicedAs } from './threadInvoices'

interface ReadyLoadItemProps {
    row: TmsInvoiceRow
    /** The load's id, from the row or from a card in the thread that named it. */
    loadId?: string
    onSend: (message: string) => void
}

/** A reason's message as one clause of the item's line. */
const clause = (message: string) => message.trim().replace(/\.$/, '')

/**
 * One Ready-tab load (plan FR-R1): number, broker, amount, status and documents on one line; what the check found in one line
 * under it, in the order it is fixed; and every action those reasons need, right here in the chat. The item is a snapshot and is
 * not read again: an action the user did here shows as done in its place, and the others stay.
 */
export const ReadyLoadItem = ({ row, loadId, onSend }: ReadyLoadItemProps) => {
    const loadNumber = row.load_numbers[0]
    // Invoiced in this chat after the item was drawn: it says so, and only the uploads a factor still asks for stay. Matched by
    // the row's own load id: another load showing the same number is not this one.
    const invoicedAs = useInvoicedAs(row.load_id, loadNumber)
    const findings = invoicedAs ? [] : findingsOf(row)
    const blocked = row.readiness === 'not_ready'
    const actions = invoicedAs ? rowActions(row).filter((step) => step.kind !== 'invoice' && step.kind !== 'set_rate') : rowActions(row)

    return (
        <li aria-label={`Load ${row.load_numbers.map((n) => `#${n}`).join(', ')}`} className="flex flex-col gap-1.5 py-2.5 first:pt-0 last:pb-0">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                <span className="text-sm font-semibold text-foreground">{row.load_numbers.map((n) => `#${n}`).join(', ') || '—'}</span>
                {row.broker && <span className="min-w-0 max-w-[14rem] truncate text-foreground-muted">{row.broker}</span>}
                <DocumentChips documents={row.documents} driverAssigned={row.driver_assigned} />
                <span className="ml-auto text-sm font-medium tabular-nums text-foreground">{formatUsd(row.amount_due_usd)}</span>
                {invoicedAs ? (
                    <span role="status" className="flex items-center gap-1 text-xs text-success">
                        <CircleCheck className="h-3.5 w-3.5 shrink-0" aria-hidden />
                        Invoiced {invoicedAs}
                    </span>
                ) : (
                    <StatusBadge row={row} />
                )}
            </div>
            {findings.length > 0 && (
                <p className={`flex items-start gap-1.5 text-xs ${blocked ? 'text-warning-soft-foreground' : 'text-foreground-muted'}`}>
                    {blocked && <CircleAlert className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />}
                    <span>
                        {findings.map((reason, i) => (
                            <span key={reason.code} title={clause(reason.message)}>
                                {i > 0 && ' · '}
                                {REASON_LABEL[reason.code] ?? clause(reason.message)}
                            </span>
                        ))}
                    </span>
                </p>
            )}
            {loadNumber && <LoadFixActions loadNumber={loadNumber} loadId={loadId} actions={actions} onSend={onSend} />}
        </li>
    )
}
